import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AlertTriangle,
  ChevronDown,
  Database,
  FileSpreadsheet,
  Folder,
  Loader2,
} from "lucide-react"

import { Badge, Button, Label, Skeleton } from "@f2e/ui/components/base"
import { useUiTranslation } from "@f2e/ui/i18n"

import { useToast } from "@/hooks"
import {
  type KnowledgeNodeSchema,
  type TabularDetailSchema,
  useBatchKnowledgeBaseNodesApi,
  useKnowledgeBaseTabularCapabilitiesApi,
  useKnowledgeBaseTabularDetailApi,
  useListKnowledgeBaseNodesApi,
} from "@/hooks/api"
import {
  getUserFacingApiErrorMessage,
  getUserFacingApiErrorToastProps,
} from "@/lib/apiErrors"
import {
  getTabularSourceKind,
  isPotentialTabularKnowledgeNode,
  isTabularReadyKnowledgeNode,
} from "@/pages/KnowledgeBase/components/tabular"

import { InvocationInstructionField } from "./InvocationInstructionField"
import type { UnifiedTool } from "./type"

interface TabularDataToolSectionProps {
  tool: UnifiedTool
  disabled?: boolean
  organizationId?: string
  workspaceId?: string
  onUpdateTool: (toolId: string, updates: Partial<UnifiedTool>) => void
  onInvalidSelectionChange?: (toolId: string, hasInvalid: boolean) => void
}

interface TabularSourceOptionProps {
  node: KnowledgeNodeSchema
  organizationId: string
  workspaceId: string
  selected: boolean
  disabled: boolean
  onSelect: (node: KnowledgeNodeSchema, detail: TabularDetailSchema) => void
  onLoadError: (error: unknown) => void
}

const TabularSourceOption = ({
  node,
  organizationId,
  workspaceId,
  selected,
  disabled,
  onSelect,
  onLoadError,
}: TabularSourceOptionProps) => {
  const { t } = useTranslation()
  const [detail, setDetail] = useState<TabularDetailSchema | null>(null)
  const { fetchFn: fetchDetail } = useKnowledgeBaseTabularDetailApi()

  useEffect(() => {
    let active = true
    fetchDetail({
      urlBuilderParams: {
        organizationId,
        workspaceId,
        kbId: node.knowledge_base_id,
        nodeId: node.id,
      },
    })
      .then((response) => {
        if (active) setDetail(response?.data ?? null)
      })
      .catch((error) => {
        console.error("Failed to resolve tabular source option", error)
        onLoadError(error)
        if (active) setDetail(null)
      })
    return () => {
      active = false
    }
  }, [
    fetchDetail,
    node.id,
    node.knowledge_base_id,
    organizationId,
    onLoadError,
    workspaceId,
  ])

  if (!detail?.available || !detail.feature_enabled) return null

  const columnCount = detail.tables.reduce(
    (count, table) => count + table.columns.length,
    0
  )
  const sourceKind = getTabularSourceKind(node)

  return (
    <button
      type="button"
      data-copilot-field={`basicForm.unifiedTools.tabularData.source.${node.id}`}
      data-copilot-value={JSON.stringify({
        knowledgeNodeId: node.id,
        name: node.name,
        sourceKind,
        tables: detail.tables.length,
        columns: columnCount,
      })}
      disabled={disabled}
      onClick={() => onSelect(node, detail)}
      className={`flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
        selected
          ? "border-primary bg-primary/5"
          : "border-transparent hover:border-border hover:bg-muted/50"
      } disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <FileSpreadsheet className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{node.name}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {t("workspace.agent.basicForm.unifiedToolSelector.tabularSummary", {
            tables: detail.tables.length,
            columns: columnCount,
          })}
        </span>
      </span>
      <Badge variant="secondary" className="shrink-0 text-[10px]">
        {t(
          `workspace.agent.basicForm.unifiedToolSelector.tabularSource.${sourceKind}`
        )}
      </Badge>
    </button>
  )
}

interface TabularFolderRowProps {
  node: KnowledgeNodeSchema
  organizationId: string
  workspaceId: string
  selectedNodeId?: string
  disabled: boolean
  onSelect: (node: KnowledgeNodeSchema, detail: TabularDetailSchema) => void
  onLoadError: (error: unknown) => void
}

const TabularFolderRow = ({
  node,
  organizationId,
  workspaceId,
  selectedNodeId,
  disabled,
  onSelect,
  onLoadError,
}: TabularFolderRowProps) => {
  const [expanded, setExpanded] = useState(false)
  const [children, setChildren] = useState<KnowledgeNodeSchema[] | null>(null)
  const [loading, setLoading] = useState(false)
  const { fetchFn: listChildren } = useListKnowledgeBaseNodesApi()

  const toggle = async () => {
    const nextExpanded = !expanded
    setExpanded(nextExpanded)
    if (!nextExpanded || children !== null) return
    setLoading(true)
    try {
      const response = await listChildren({
        urlBuilderParams: { organizationId, workspaceId },
        searchParams: new URLSearchParams({ parent_id: node.id }),
      })
      setChildren(response?.data ?? [])
    } catch (error) {
      console.error("Failed to list tabular source folder", error)
      onLoadError(error)
      setChildren([])
    } finally {
      setLoading(false)
    }
  }

  const visibleChildren = (children ?? []).filter(
    (child) =>
      child.node_type === "FOLDER" ||
      (isPotentialTabularKnowledgeNode(child) &&
        isTabularReadyKnowledgeNode(child))
  )

  return (
    <div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-full justify-start gap-2 px-2"
        onClick={() => void toggle()}
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <ChevronDown
            className={`size-4 transition-transform ${expanded ? "" : "-rotate-90"}`}
          />
        )}
        <Folder className="size-4 text-amber-500" />
        <span className="truncate">{node.name}</span>
      </Button>
      {expanded && (
        <div className="ml-4 border-l border-border pl-2">
          {visibleChildren.map((child) =>
            child.node_type === "FOLDER" ? (
              <TabularFolderRow
                key={child.id}
                node={child}
                organizationId={organizationId}
                workspaceId={workspaceId}
                selectedNodeId={selectedNodeId}
                disabled={disabled}
                onSelect={onSelect}
                onLoadError={onLoadError}
              />
            ) : (
              <TabularSourceOption
                key={child.id}
                node={child}
                organizationId={organizationId}
                workspaceId={workspaceId}
                selected={selectedNodeId === child.id}
                disabled={disabled}
                onSelect={onSelect}
                onLoadError={onLoadError}
              />
            )
          )}
        </div>
      )}
    </div>
  )
}

export const TabularDataToolSection = ({
  tool,
  disabled = false,
  organizationId,
  workspaceId,
  onUpdateTool,
  onInvalidSelectionChange,
}: TabularDataToolSectionProps) => {
  const { t } = useTranslation()
  const { t: uiT } = useUiTranslation()
  const { alertToast } = useToast()
  const [roots, setRoots] = useState<KnowledgeNodeSchema[]>([])
  const [selectedNode, setSelectedNode] = useState<KnowledgeNodeSchema | null>(
    null
  )
  const [selectedDetail, setSelectedDetail] =
    useState<TabularDetailSchema | null>(null)
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null)
  const [featureLoadComplete, setFeatureLoadComplete] = useState(false)
  const [resolvedSelectionKey, setResolvedSelectionKey] = useState<
    string | null
  >(null)
  const [loadError, setLoadError] = useState<unknown>()
  const { fetchFn: fetchCapabilities } =
    useKnowledgeBaseTabularCapabilitiesApi()
  const { fetchFn: listRoots } = useListKnowledgeBaseNodesApi()
  const { fetchFn: resolveSelectedNodes } = useBatchKnowledgeBaseNodesApi()
  const { fetchFn: fetchSelectedDetail } = useKnowledgeBaseTabularDetailApi()
  const selectedRequestRef = useRef(0)

  useEffect(() => {
    if (!organizationId || !workspaceId) {
      setFeatureLoadComplete(false)
      return
    }
    let active = true
    setFeatureLoadComplete(false)
    setLoadError(undefined)
    Promise.all([
      fetchCapabilities({
        urlBuilderParams: { organizationId, workspaceId },
      }),
      listRoots({ urlBuilderParams: { organizationId, workspaceId } }),
    ])
      .then(([capabilities, rootResponse]) => {
        if (!active) return
        setFeatureEnabled(capabilities?.data?.enabled === true)
        setRoots(
          (rootResponse?.data ?? []).filter(
            (node) => node.node_type === "FOLDER"
          )
        )
      })
      .catch((error) => {
        if (!active) return
        console.error("Failed to load tabular tool sources", error)
        setLoadError(error)
        setFeatureEnabled(null)
      })
      .finally(() => {
        if (active) setFeatureLoadComplete(true)
      })
    return () => {
      active = false
    }
  }, [fetchCapabilities, listRoots, organizationId, workspaceId])

  const selectionKey =
    organizationId && workspaceId && tool.knowledgeNodeId
      ? `${organizationId}:${workspaceId}:${tool.knowledgeNodeId}`
      : null

  useEffect(() => {
    const requestId = ++selectedRequestRef.current
    if (!organizationId || !workspaceId || !tool.knowledgeNodeId) {
      setSelectedNode(null)
      setSelectedDetail(null)
      setResolvedSelectionKey(null)
      return
    }

    const requestSelectionKey = `${organizationId}:${workspaceId}:${tool.knowledgeNodeId}`
    if (resolvedSelectionKey === requestSelectionKey) return

    setSelectedNode(null)
    setSelectedDetail(null)

    resolveSelectedNodes({
      urlBuilderParams: { organizationId, workspaceId },
      jsonBody: {
        ids: [tool.knowledgeNodeId],
        include_deleted: true,
        include_ancestors: false,
      },
    })
      .then(async (response) => {
        const node = response?.data?.find(
          (candidate) => candidate.id === tool.knowledgeNodeId
        )
        if (requestId !== selectedRequestRef.current || !node) return
        setSelectedNode(node)
        if (!isTabularReadyKnowledgeNode(node)) return
        const detailResponse = await fetchSelectedDetail({
          urlBuilderParams: {
            organizationId,
            workspaceId,
            kbId: node.knowledge_base_id,
            nodeId: node.id,
          },
        })
        if (requestId !== selectedRequestRef.current) return
        setSelectedDetail(detailResponse?.data ?? null)
      })
      .catch((error) => {
        if (requestId !== selectedRequestRef.current) return
        console.error("Failed to resolve selected tabular source", error)
        setLoadError(error)
      })
      .finally(() => {
        if (requestId !== selectedRequestRef.current) return
        setResolvedSelectionKey(requestSelectionKey)
      })
  }, [
    fetchSelectedDetail,
    organizationId,
    resolvedSelectionKey,
    resolveSelectedNodes,
    tool.knowledgeNodeId,
    workspaceId,
  ])

  const isResolvingSavedSelection =
    Boolean(tool.knowledgeNodeId) &&
    featureEnabled !== false &&
    (!featureLoadComplete ||
      selectionKey === null ||
      resolvedSelectionKey !== selectionKey)
  const invalidSelection =
    !isResolvingSavedSelection &&
    (featureEnabled !== true ||
      !tool.knowledgeNodeId ||
      !selectedNode ||
      !isTabularReadyKnowledgeNode(selectedNode) ||
      !selectedDetail?.available)

  useEffect(() => {
    onInvalidSelectionChange?.(tool.id, invalidSelection)
  }, [invalidSelection, onInvalidSelectionChange, tool.id])

  const handleSelect = (
    node: KnowledgeNodeSchema,
    detail: TabularDetailSchema
  ) => {
    setSelectedNode(node)
    setSelectedDetail(detail)
    setResolvedSelectionKey(
      organizationId && workspaceId
        ? `${organizationId}:${workspaceId}:${node.id}`
        : null
    )
    onUpdateTool(tool.id, { knowledgeNodeId: node.id })
  }
  const handleLoadError = useCallback(
    (error: unknown) => setLoadError(error),
    []
  )

  const warningCount = useMemo(
    () =>
      (selectedDetail?.warnings.length ?? 0) +
      (selectedDetail?.tables.reduce(
        (count, table) => count + table.warnings.length,
        0
      ) ?? 0),
    [selectedDetail]
  )
  const selectedWarnings = useMemo(
    () => [
      ...(selectedDetail?.warnings ?? []),
      ...(selectedDetail?.tables.flatMap((table) => table.warnings) ?? []),
    ],
    [selectedDetail]
  )
  const selectedSourceKind = selectedNode
    ? getTabularSourceKind(selectedNode)
    : null

  const loadErrorMessage = loadError
    ? getUserFacingApiErrorMessage(
        loadError,
        t,
        t("workspace.agent.basicForm.unifiedToolSelector.tabularLoadFailed")
      )
    : null

  useEffect(() => {
    if (!loadError) return
    alertToast({
      ...getUserFacingApiErrorToastProps(loadError, t, {
        title: t(
          "workspace.agent.basicForm.unifiedToolSelector.tabularLoadFailed"
        ),
        fallbackDescription: t(
          "workspace.agent.basicForm.unifiedToolSelector.tabularLoadFailed"
        ),
      }),
    })
  }, [alertToast, loadError, t])

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        {t("workspace.agent.basicForm.unifiedToolSelector.tabularReadOnlyHelp")}
      </div>

      <div className="space-y-2">
        <Label>
          {t(
            "workspace.agent.basicForm.unifiedToolSelector.tabularSourceLabel"
          )}
        </Label>
        {isResolvingSavedSelection ? (
          <div
            role="status"
            aria-live="polite"
            aria-label={t(
              "workspace.agent.basicForm.unifiedToolSelector.tabularBindingLoading"
            )}
            className="rounded-lg border border-border bg-background p-3"
          >
            <div className="flex items-start gap-3">
              <Skeleton className="mt-0.5 size-4 shrink-0 rounded-sm" />
              <div className="min-w-0 flex-1">
                <Skeleton className="h-4 w-40 max-w-full" />
                <Skeleton className="mt-2 h-3 w-24 max-w-full" />
              </div>
              <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
            </div>
          </div>
        ) : selectedNode && selectedDetail?.available ? (
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="flex items-start gap-3">
              <Database className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {selectedNode.name}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(
                    "workspace.agent.basicForm.unifiedToolSelector.tabularSummary",
                    {
                      tables: selectedDetail.tables.length,
                      columns: selectedDetail.tables.reduce(
                        (count, table) => count + table.columns.length,
                        0
                      ),
                    }
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {selectedSourceKind && (
                  <Badge variant="secondary" className="text-[10px]">
                    {t(
                      `workspace.agent.basicForm.unifiedToolSelector.tabularSource.${selectedSourceKind}`
                    )}
                  </Badge>
                )}
                {warningCount > 0 && (
                  <Badge variant="outline" className="gap-1 text-amber-700">
                    <AlertTriangle className="size-3" />
                    {warningCount}
                  </Badge>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-2 border-t border-border pt-3">
              {selectedDetail.tables.map((table) => (
                <div key={table.id} className="text-xs">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate font-medium">{table.name}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {t(
                        "workspace.agent.basicForm.unifiedToolSelector.tabularRows",
                        { count: table.row_count }
                      )}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-muted-foreground">
                    {table.columns
                      .slice(0, 4)
                      .map((column) => `${column.name} (${column.type})`)
                      .join(" · ")}
                    {table.columns.length > 4 &&
                      ` · ${t(
                        "workspace.agent.basicForm.unifiedToolSelector.tabularMoreColumns",
                        { count: table.columns.length - 4 }
                      )}`}
                  </p>
                </div>
              ))}
            </div>
            {selectedWarnings.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-border pt-3">
                {selectedWarnings.map((warning, index) => (
                  <div
                    key={`${warning.code}-${index}`}
                    className="flex gap-2 text-xs text-amber-700 dark:text-amber-300"
                  >
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <div>
                      <p>
                        {uiT(
                          `chat.reasoningSteps.tabular.warnings.${warning.code}`,
                          warning.message
                        )}
                      </p>
                      {warning.recovery_hint && (
                        <p className="mt-0.5 opacity-80">
                          {uiT(
                            `chat.reasoningSteps.tabular.warningRecovery.${warning.code}`,
                            warning.recovery_hint
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tool.knowledgeNodeId ? (
          <div className="flex gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-xs text-destructive">
            <AlertTriangle className="size-4 shrink-0" />
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.tabularInvalidBinding"
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.tabularSelectRequired"
            )}
          </p>
        )}
      </div>

      {featureEnabled === true && (
        <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
          {roots.map((root) => (
            <TabularFolderRow
              key={root.id}
              node={root}
              organizationId={organizationId!}
              workspaceId={workspaceId!}
              selectedNodeId={tool.knowledgeNodeId}
              disabled={disabled}
              onSelect={handleSelect}
              onLoadError={handleLoadError}
            />
          ))}
          {roots.length === 0 && (
            <p className="p-3 text-center text-xs text-muted-foreground">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.noTabularSources"
              )}
            </p>
          )}
        </div>
      )}

      {featureEnabled === false && (
        <p className="text-xs text-muted-foreground">
          {t(
            "workspace.agent.basicForm.unifiedToolSelector.tabularUnavailable"
          )}
        </p>
      )}
      {loadErrorMessage && (
        <p className="text-xs text-destructive">{loadErrorMessage}</p>
      )}

      <InvocationInstructionField
        id={`tabular-invocation-${tool.id}`}
        placeholder={t(
          "workspace.agent.basicForm.unifiedToolSelector.tabularInvocationInstructionPlaceholder"
        )}
        value={tool.invocationInstruction}
        onChange={(value) =>
          onUpdateTool(tool.id, { invocationInstruction: value })
        }
        disabled={disabled}
      />
    </div>
  )
}
